// modules/notifications/ui.ts — <app-notifications>
// Sistema de toasts. Escucha 'notifications:show' del bus.

import { useBus } from '../../shared/hooks/use-bus';

const TYPE_STYLES: Record<string, string> = {
  success: 'var(--color-success)',
  error:   'var(--color-error)',
  warning: 'var(--color-warning)',
  info:    'var(--color-primary)',
};

export class AppNotifications extends HTMLElement {
  static define() { customElements.define('app-notifications', AppNotifications); }

  connectedCallback() {
    this.style.cssText = `
      position: fixed; bottom: calc(var(--tab-bar-height) + var(--safe-bottom) + 12px);
      left: 50%; transform: translateX(-50%);
      z-index: var(--z-toast); display: flex; flex-direction: column;
      align-items: center; gap: var(--space-2); pointer-events: none;
      width: min(90vw, 400px);
    `;

    useBus('notifications:show', ({ message, type = 'info', duration = 4000, action }) => {
      this.showToast(message, type, duration, action);
    }, this);
  }

  private showToast(
    message: string,
    type: string,
    duration: number,
    action?: { label: string; onClick: () => void },
  ) {
    const toast = document.createElement('div');
    const color = TYPE_STYLES[type] ?? TYPE_STYLES.info;
    const persistent = duration <= 0;

    toast.style.cssText = `
      background: var(--color-surface); border: 1px solid ${color};
      border-left: 4px solid ${color};
      color: var(--color-text); padding: var(--space-3) var(--space-4);
      border-radius: var(--radius-lg); font-size: var(--text-sm);
      font-family: var(--font-body); font-weight: var(--font-weight-medium);
      box-shadow: var(--shadow-lg); pointer-events: auto;
      transform: translateY(8px); opacity: 0;
      transition: all var(--duration-normal) var(--ease-decelerate);
      max-width: 100%; width: 100%; box-sizing: border-box; word-break: break-word;
      display: flex; align-items: center; gap: var(--space-3);
    `;

    const text = document.createElement('span');
    text.textContent = message;
    text.style.cssText = 'flex: 1; min-width: 0;';
    toast.appendChild(text);

    const dismiss = () => {
      toast.style.transform = 'translateY(-8px)';
      toast.style.opacity   = '0';
      setTimeout(() => toast.remove(), 300);
    };

    if (action) {
      const btn = document.createElement('button');
      btn.textContent = action.label;
      btn.style.cssText = `
        flex-shrink: 0; cursor: pointer;
        background: ${color}; color: var(--color-primary-text); border: none;
        border-radius: var(--radius-sm); padding: var(--space-2) var(--space-3);
        font-family: var(--font-body); font-size: var(--text-xs);
        font-weight: var(--font-weight-semibold);
      `;
      btn.addEventListener('click', () => { action.onClick(); dismiss(); });
      toast.appendChild(btn);
    }

    // Toasts persistentes: botón de cierre manual (no se van solos).
    if (persistent) {
      const close = document.createElement('button');
      close.textContent = '✕';
      close.setAttribute('aria-label', 'Cerrar');
      close.style.cssText = `
        flex-shrink: 0; cursor: pointer; background: transparent; border: none;
        color: var(--color-text-muted); font-size: var(--text-base);
        line-height: 1; padding: var(--space-1);
      `;
      close.addEventListener('click', dismiss);
      toast.appendChild(close);
    }

    this.appendChild(toast);

    // Animar entrada
    requestAnimationFrame(() => {
      toast.style.transform = 'translateY(0)';
      toast.style.opacity   = '1';
    });

    // Salida automática (solo si no es persistente)
    if (!persistent) setTimeout(dismiss, duration);
  }
}
